import React, { useState, useEffect } from 'react';

import { makeStyles } from '@material-ui/core/styles';

import IconButton from "@material-ui/core/IconButton";
import Close from "@material-ui/icons/Close";
import Button from "@material-ui/core/Button";
import Modal from "@material-ui/core/Modal";
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PhoneIcon from '@material-ui/icons/Phone';
import BusinessIcon from '@material-ui/icons/Business';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';

const YELLOW = '#edd714';
const YELLOW_DIM = 'rgba(237, 215, 20, 0.12)';
const GREEN = '#4CAF50';

const useStyles = makeStyles((theme) => ({
    modalContainer: {
        backgroundColor: '#0d0d0d',
        width: '100%',
        maxWidth: '520px',
        borderRadius: '20px',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        outline: 'none',
    },
    glowBar: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, transparent, ${YELLOW}, transparent)`,
    },
    closeButton: {
        color: '#555',
        position: 'absolute',
        top: '12px',
        right: '12px',
        padding: '8px',
        '&:hover': {
            color: '#fff',
            backgroundColor: 'rgba(255,255,255,0.05)',
        },
    },
    content: {
        padding: '48px 40px 40px',
        width: '100%',
        boxSizing: 'border-box',
    },
    iconWrap: {
        width: '72px',
        height: '72px',
        borderRadius: '50%',
        backgroundColor: YELLOW_DIM,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
    },
    icon: {
        fontSize: '32px !important',
        color: YELLOW,
    },
    title: {
        fontFamily: 'Orbitron, sans-serif',
        fontSize: '28px',
        letterSpacing: '3px',
        color: '#ffffff',
        textAlign: 'center',
        marginBottom: '8px',
    },
    subtitle: {
        fontFamily: 'Exo 2',
        fontSize: '13px',
        fontWeight: 500,
        color: '#888',
        textAlign: 'center',
        lineHeight: 1.7,
        marginBottom: '28px',
        maxWidth: '380px',
        margin: '0 auto 28px',
    },
    companyName: {
        color: YELLOW,
        fontWeight: 700,
    },
    // Countdown timer
    timerContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginBottom: '28px',
    },
    timerBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
    },
    timerValue: {
        fontFamily: 'Orbitron, sans-serif',
        fontSize: '36px',
        color: YELLOW,
        lineHeight: 1,
        minWidth: '52px',
        textAlign: 'center',
        backgroundColor: '#1a1a1a',
        border: '1px solid #222',
        borderRadius: '10px',
        padding: '12px 8px',
    },
    timerLabel: {
        fontFamily: 'Exo 2',
        fontSize: '9px',
        fontWeight: 700,
        letterSpacing: '1px',
        color: '#555',
        textTransform: 'uppercase',
    },
    timerSeparator: {
        fontFamily: 'Orbitron',
        fontSize: '28px',
        color: '#333',
        alignSelf: 'flex-start',
        paddingTop: '12px',
    },
    // Verification checklist
    checklist: {
        borderTop: '1px solid #1a1a1a',
        paddingTop: '24px',
        marginBottom: '24px',
    },
    checklistTitle: {
        fontFamily: 'Exo 2',
        fontSize: '11px',
        fontWeight: 800,
        letterSpacing: '1px',
        color: '#666',
        textTransform: 'uppercase',
        marginBottom: '14px',
    },
    checkRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 0',
    },
    checkIcon: {
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    checkVerified: {
        backgroundColor: `${GREEN}18`,
        color: GREEN,
    },
    checkPending: {
        backgroundColor: 'rgba(237, 215, 20, 0.1)',
        color: YELLOW,
    },
    checkLabel: {
        fontFamily: 'Exo 2',
        fontSize: '13px',
        fontWeight: 600,
        color: '#ccc',
    },
    checkLabelPending: {
        color: '#888',
    },
    checkStatus: {
        fontFamily: 'Exo 2',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.5px',
        marginLeft: 'auto',
    },
    statusComplete: {
        color: GREEN,
    },
    statusPending: {
        color: YELLOW,
    },
    // Actions
    contactButton: {
        fontFamily: 'Exo 2',
        fontWeight: 800,
        fontSize: '12px',
        letterSpacing: '1.5px',
        borderRadius: '10px',
        height: '46px',
        width: '100%',
        textTransform: 'uppercase',
        backgroundColor: 'transparent',
        color: '#888',
        border: '1px solid #333',
        transition: 'all 0.2s ease',
        marginBottom: '12px',
        '&:hover': {
            borderColor: '#555',
            color: '#fff',
            backgroundColor: 'rgba(255,255,255,0.03)',
        },
    },
    supportNote: {
        fontFamily: 'Exo 2',
        fontSize: '11px',
        fontWeight: 500,
        color: '#555',
        textAlign: 'center',
        lineHeight: 1.6,
    },
    supportEmail: {
        color: YELLOW,
        textDecoration: 'none',
        fontWeight: 700,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    // Verified state
    verifiedBanner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px 20px',
        backgroundColor: `${GREEN}10`,
        borderRadius: '10px',
        border: `1px solid ${GREEN}25`,
        marginBottom: '24px',
    },
    verifiedText: {
        fontFamily: 'Exo 2',
        fontSize: '13px',
        fontWeight: 700,
        color: GREEN,
    },
}));

/**
 * Unverified
 *
 * Premium modal shown to org users whose verification is still pending.
 * Shows a live countdown timer for the 1-hour verification hold period
 * and a verification checklist.
 *
 * Props:
 *   modalOpen       — boolean
 *   setModalOpen    — function to toggle
 *   userData        — user profile data (includes verificationDelayUntil, companyVerification, etc.)
 */
export default function Unverified(props) {

    const classes = useStyles();
    const { userData = {} } = props;

    // ── Countdown timer ─────────────────────────────────────────────────
    const delayUntil = userData.verificationDelayUntil
        ? new Date(userData.verificationDelayUntil).getTime()
        : Date.now() + 3600000;

    const [timeLeft, setTimeLeft] = useState(() => {
        const diff = delayUntil - Date.now();
        return diff > 0 ? diff : 0;
    });

    useEffect(() => {
        if (timeLeft <= 0) return;
        const interval = setInterval(() => {
            const diff = delayUntil - Date.now();
            setTimeLeft(diff > 0 ? diff : 0);
        }, 1000);
        return () => clearInterval(interval);
    }, [delayUntil, timeLeft]);

    const minutes = Math.floor(timeLeft / 60000);
    const seconds = Math.floor((timeLeft % 60000) / 1000);
    const isExpired = timeLeft <= 0;

    const companyName = userData.companyVerification?.companyName
        || userData.companyName
        || userData.collectiveName
        || userData.agencyName
        || 'your organization';

    const handleClose = () => {
        props.setModalOpen(false);
    };

    // ── Verification checks ─────────────────────────────────────────────
    const checks = [
        { icon: <MailOutlineIcon style={{ fontSize: '16px' }} />, label: 'Email Verified', complete: true },
        { icon: <PhoneIcon style={{ fontSize: '16px' }} />, label: 'Phone Verified', complete: true },
        { icon: <BusinessIcon style={{ fontSize: '16px' }} />, label: 'Company Information', complete: !!userData.companyVerification },
        { icon: <VerifiedUserIcon style={{ fontSize: '16px' }} />, label: 'Account Review', complete: userData.verified === true },
    ];

    return (
        <Modal open={props.modalOpen}
               onClose={() => { props.setModalOpen(false)}}
               style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
               disableAutoFocus
               disableEnforceFocus
        >
            <div className={classes.modalContainer}>
                <div className={classes.glowBar} />

                <IconButton className={classes.closeButton} onClick={handleClose}>
                    <Close style={{ fontSize: '20px' }} />
                </IconButton>

                <div className={classes.content}>
                    <div className={classes.iconWrap}>
                        {isExpired && userData.verified
                            ? <CheckCircleOutlineIcon className={classes.icon} style={{ color: GREEN }} />
                            : <HourglassEmptyIcon className={classes.icon} />
                        }
                    </div>

                    <div className={classes.title}>
                        {isExpired && userData.verified ? 'ACCOUNT VERIFIED' : 'VERIFICATION IN PROGRESS'}
                    </div>
                    <div className={classes.subtitle}>
                        {isExpired && userData.verified
                            ? <>Your account for <span className={classes.companyName}>{companyName}</span> has been verified. You now have full access to the platform.</>
                            : <>Thank you for signing up with <span className={classes.companyName}>{companyName}</span>. To protect the privacy of our athletes, all business accounts are verified before receiving full access.</>
                        }
                    </div>

                    {/* Countdown Timer */}
                    {!isExpired && (
                        <div className={classes.timerContainer}>
                            <div className={classes.timerBlock}>
                                <div className={classes.timerValue}>
                                    {String(minutes).padStart(2, '0')}
                                </div>
                                <div className={classes.timerLabel}>Minutes</div>
                            </div>
                            <div className={classes.timerSeparator}>:</div>
                            <div className={classes.timerBlock}>
                                <div className={classes.timerValue}>
                                    {String(seconds).padStart(2, '0')}
                                </div>
                                <div className={classes.timerLabel}>Seconds</div>
                            </div>
                        </div>
                    )}

                    {/* Verified banner */}
                    {isExpired && userData.verified && (
                        <div className={classes.verifiedBanner}>
                            <CheckCircleOutlineIcon style={{ fontSize: '18px', color: GREEN }} />
                            <span className={classes.verifiedText}>All checks passed</span>
                        </div>
                    )}

                    {/* Verification Checklist */}
                    <div className={classes.checklist}>
                        <div className={classes.checklistTitle}>Verification Checklist</div>
                        {checks.map((check, i) => (
                            <div key={i} className={classes.checkRow}>
                                <div className={`${classes.checkIcon} ${check.complete ? classes.checkVerified : classes.checkPending}`}>
                                    {check.icon}
                                </div>
                                <span className={`${classes.checkLabel} ${!check.complete ? classes.checkLabelPending : ''}`}>
                                    {check.label}
                                </span>
                                <span className={`${classes.checkStatus} ${check.complete ? classes.statusComplete : classes.statusPending}`}>
                                    {check.complete ? 'Complete' : 'Pending'}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Contact Support */}
                    <Button
                        href="mailto:customersupport@athleteslaunch.com"
                        className={classes.contactButton}
                    >
                        Contact Support
                    </Button>

                    <div className={classes.supportNote}>
                        Questions? Reach us at{' '}
                        <a href="mailto:customersupport@athleteslaunch.com" className={classes.supportEmail}>
                            customersupport@athleteslaunch.com
                        </a>
                    </div>
                </div>
            </div>
        </Modal>
    );
}